import { createEffect, createMemo, createSignal, onMount, ParentProps } from "solid-js";
import { PostsContext } from "./context";
import { Post, PostTopic } from "../types/post";
import { fetchPosts } from "../repository/post";
import { useSearchParams } from "~/primitives/searchparams";
import { fetchPostTopics } from "../repository/topic";

export interface PostsProviderProps {
	maxPerPage?: number;
}

export function PostsProvider(props: ParentProps<PostsProviderProps>) {

	const [maxPerPage, setMaxPerPage] = createSignal<number>(0);

	const [posts, setPosts] = createSignal<Post[]>([]);
	const [topics, setTopics] = createSignal<PostTopic[]>([]);
	const [page, setPage] = createSignal<number>(1);
	const [search, setSearch] = createSignal<string>('');
	const [topic, setTopic] = createSignal<string | undefined>(undefined);
	const [selected, setSelected] = createSignal<Post>();
	const [status, setStatus] = createSignal<"idle" | "loading">("loading");
	const [searchParams, setSearchParams] = useSearchParams<{ post?: string }>();

	onMount(async () => {

		setMaxPerPage(props.maxPerPage || 6);

		setStatus("loading");

		const [
			_posts
		] = await Promise.all([
			fetchPosts().then(setPosts),
			fetchPostTopics().then(setTopics),
		]);
		const param = searchParams.get('post');

		if (!param) {
			setStatus("idle");
			return;
		}

		const post = _posts.find(p => p.id === param);

		if (post) {
			setSelected(post);
		} else {
			unselect();
		}

		setStatus("idle");
	});

	const select = (id: string) => {
		const post = posts().find(p => p.id === id);

		if (post) {
			setSelected(post);
			setSearchParams('post', post.id);
		}
	};

	const unselect = () => {
		setSelected(undefined);
		setSearchParams('post', undefined);
	};

	const filteredPosts = createMemo(() => {

		const _posts = posts();
		const _search = search()?.toLowerCase();
		const _topic = topic();

		if (!_search && !_topic) {
			return _posts;
		}

		return _posts.filter(p => {
			const title = p.title.toLowerCase();
			const topic = p.topic?.id;
			const content = p.content.toLowerCase();

			return (
				!_search ? true : title.includes(_search) || content.includes(_search)
			) && (
				!_topic ? true : _topic === topic || (
					_topic === "rest" && !topic
				)
			);
		});
	});

	const pageCount = createMemo(() => {
		const _maxPerPage = maxPerPage();
		const total = filteredPosts().length;
		const count = Math.ceil(total / _maxPerPage);

		if (Number.isNaN(count) || count === 0) {
			return 1;
		}

		return count;
	});

	const paginatedPosts = createMemo(() => {
		const _maxPerPage = maxPerPage();
		const _posts = filteredPosts();

		if (_posts.length === 0) {
			return [];
		}

		const start = (page() - 1) * _maxPerPage;
		const end = start + _maxPerPage;

		return _posts.slice(start, end);
	});

	createEffect(() => {
		search();
		setPage(1);
	});

	const isSelected = createMemo<boolean>(() => {
		return !!searchParams.get('post');
	});

	return (
		<PostsContext.Provider
			value={{
				posts: paginatedPosts,
				search,
				setSearch,
				setTopic,
				topic,
				page,
				setPage,
				select,
				selected,
				unselect,
				status,
				pageCount,
				isSelected,
				topics,
			}}
		>
			{props.children}
		</PostsContext.Provider>
	);
}